import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { PasswordField } from '@shared/auth/components/PasswordField';
import { authService } from '@shared/services/auth.service';
import { clearRevokedSession } from '@shared/hooks/useAuth';
import { isApiError } from '@shared/api';
import { PATHS } from '@shared/router/paths';
import { PageHeader } from '@shared/components/layout/page-header';

/**
 * identity-service `ChangePasswordDto`: `currentPassword` (string) and
 * `newPassword` (MinLength 8). Those are the only rules; the confirmation
 * field is a typing aid, not a policy.
 */
const schema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
type FormValues = z.infer<typeof schema>;

/**
 * `PATCH /auth/change-password` revokes EVERY session of the account — this
 * one included ("Password changed. Please log in again."). So on success the
 * local session is cleared and the user signs in again; staying on the page
 * would leave them on a revoked session.
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const submit = async ({ currentPassword, newPassword }: FormValues) => {
    try {
      await authService.changePassword({ currentPassword, newPassword });
      clearRevokedSession();
      navigate(`${PATHS.auth.login}?reason=password-changed`, { replace: true });
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        form.setError('currentPassword', { message: 'The current password is incorrect.' });
        return;
      }
      const field = isApiError(error) ? error.fieldError('newPassword') : undefined;
      if (field) {
        form.setError('newPassword', { message: field });
        return;
      }
      form.setError('root', {
        message: error instanceof Error ? error.message : 'Unable to change password',
      });
    }
  };

  const loading = form.formState.isSubmitting;
  const rootError = form.formState.errors.root?.message;

  return (
    <div className="mx-auto w-full max-w-md">
      <PageHeader
        title="Change password"
        description="Every device signed in to this account will be signed out."
      />
      {rootError ? (
        <p
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {rootError}
        </p>
      ) : null}
      <form onSubmit={form.handleSubmit(submit)} className="space-y-4" noValidate>
        <PasswordField
          id="currentPassword"
          label="Current password"
          autoComplete="current-password"
          error={form.formState.errors.currentPassword?.message}
          {...form.register('currentPassword')}
        />
        <PasswordField
          id="newPassword"
          label="New password"
          autoComplete="new-password"
          error={form.formState.errors.newPassword?.message}
          {...form.register('newPassword')}
        />
        <PasswordField
          id="confirmPassword"
          label="Confirm new password"
          autoComplete="new-password"
          error={form.formState.errors.confirmPassword?.message}
          {...form.register('confirmPassword')}
        />
        <button
          type="submit"
          disabled={loading}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
          Update password
        </button>
      </form>
    </div>
  );
}
